import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Button, Col, Row, Modal, Form as RBForm } from "react-bootstrap";
import { FiEye, FiEyeOff, FiMail, FiInfo, FiSend } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  ErrorUnexpected,
  OperationSuccessfully,
} from "../../../../../const/Messages.js";
import * as EmailDeliveryConfigurationService from "../../../../../service/EmailDeliveryConfigurationService.js";
import Loading from "../../../../Common/Loading/Loading.js";
import "./EmailDeliveryConfiguration.css";

const InputGroup = ({ label, name, type = "text", ...props }) => (
  <div className="form-group row mb-1 align-items-center">
    <label
      className="col-sm-12 col-md-4 col-lg-3 col-form-label"
      style={{ textAlign: "left" }}
    >
      {label}
    </label>
    <div className="col-sm-12 col-md-7 col-lg-6">
      <Field className="form-control" name={name} type={type} {...props} />
      <ErrorMessage name={name} component="div" className="text-danger mt-1" />
    </div>
  </div>
);

export default function EmailDeliveryConfiguration() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);

  const [values, setValues] = useState({
    name: "",
    smtpHost: "",
    port: "",
    security: "NONE",
    useTlsSsl: false,
    username: "",
    password: "",
    fromEmail: "",
    senderName: "",
    active: true,
  });

  const [visibled, setVisibled] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [showTestModal, setShowTestModal] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState(null);

  const schema = Yup.object().shape({
    name: Yup.string().required("Configuration name is a required field"),
    smtpHost: Yup.string().required("SMTP host is a required field"),
    port: Yup.string().required("Port is a required field"),
    security: Yup.string().required("Security is a required field"),
    useTlsSsl: Yup.string().required("Use TLS/SSL is a required field"),
    username: Yup.string().required("SMTP username is a required field"),
    password: Yup.string().required("Password is a required field"),
    fromEmail: Yup.string().required("From email is a required field"),
    senderName: Yup.string().required("Sender name is a required field"),
    active: Yup.string().required("Active is a required field"),
  });

  useEffect(() => {
    EmailDeliveryConfigurationService.find()
      .then((data) => {
        setValues({
          name: data.name || "",
          smtpHost: data.smtpHost || "",
          port: data.port || "",
          security: data.security || "NONE",
          useTlsSsl: data.useTlsSsl || false,
          username: data.username || "",
          password: data.password || "",
          fromEmail: data.fromEmail || "",
          senderName: data.senderName || "",
          active: data.active || false,
        });
      })
      .catch((error) => {
        if (error) {
          toast.error(ErrorUnexpected);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  function onSubmit(values, actions) {
    EmailDeliveryConfigurationService.merge(values)
      .then(() => {
        toast.success(OperationSuccessfully);
        history.push("/settings");
      })
      .catch((error) => {
        if (!error || !error.response || !error.response.data) {
          toast.error(ErrorUnexpected);
        } else if (error.response.data.fields) {
          error.response.data.fields.forEach((field) => {
            actions.setFieldError(field.name, field.message);
          });
        } else if (error.response.data.title) {
          toast.error(error.response.data.title);
        } else {
          toast.error(ErrorUnexpected);
        }
      })
      .finally(() => actions.setSubmitting(false));
  }

  function handleTestConnection() {
    setIsTesting(true);
    const payload = {
      ...currentFormValues,
      recipientEmail,
    };
    EmailDeliveryConfigurationService.testConnection(payload)
      .then(() => {
        toast.success(
          "Test e-mail sent successfully. Verify if the recipient received the message."
        );
        setShowTestModal(false);
        setRecipientEmail("");
      })
      .catch((error) => {
        if (error && error.response && error.response.data && error.response.data.title) {
          toast.error(error.response.data.title);
        } else {
          toast.error("Failed to send test e-mail.");
        }
      })
      .finally(() => setIsTesting(false));
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Row>
      <Col>
        <div className="ibox">
          <div className="ibox-title">
            <h5>Setup - Email Delivery Configuration (SMTP)</h5>
          </div>
          <div className="ibox-content">
            <Formik
              initialValues={values}
              validationSchema={schema}
              onSubmit={onSubmit}
            >
              {({ isSubmitting, values }) => (
                <Form autoComplete="off">
                  <InputGroup label="Configuration Name:" name="name" />

                  <div className="hr-line-dashed my-2"></div>

                  <h5 className="font-weight-bold my-2">Server Settings</h5>
                  <InputGroup label="SMTP Host:" name="smtpHost" />
                  <InputGroup
                    label="Port:"
                    name="port"
                    type="number"
                    min="0"
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e") {
                        e.preventDefault();
                      }
                    }}
                  />
                  <div className="form-group row mb-1 align-items-center">
                    <label
                      className="col-sm-12 col-md-4 col-lg-3 col-form-label"
                      style={{ textAlign: "left" }}
                    >
                      Security:
                    </label>
                    <div className="col-sm-12 col-md-7 col-lg-6 d-flex align-items-center">
                      <Field
                        as="select"
                        className="form-control"
                        name="security"
                        style={{
                          flex: 1,
                          marginRight: "20px",
                          cursor: "pointer",
                        }}
                      >
                        <option value="NONE">NONE</option>
                        <option value="STARTTLS">STARTTLS</option>
                      </Field>
                      <Field name="useTlsSsl">
                        {({ field }) => (
                          <RBForm.Check
                            type="switch"
                            id="use-tls-ssl-switch"
                            label="Use TLS/SSL"
                            checked={field.value}
                            {...field}
                            className="custom-switch-modified text-nowrap"
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className="hr-line-dashed my-2"></div>

                  <h5 className="font-weight-bold my-2">Authentication</h5>
                  <InputGroup label="SMTP Username:" name="username" />
                  <div className="form-group row mb-1 align-items-center">
                    <label
                      className="col-sm-12 col-md-4 col-lg-3 col-form-label"
                      style={{ textAlign: "left" }}
                    >
                      Password:
                    </label>
                    <div className="col-sm-12 col-md-7 col-lg-6">
                      <div className="input-group">
                        <Field
                          className="form-control"
                          name="password"
                          type={visibled ? "text" : "password"}
                        />
                        <div
                          className="input-group-append"
                          onClick={() => setVisibled(!visibled)}
                        >
                          <span
                            className="input-group-text"
                            style={{ cursor: "pointer" }}
                          >
                            {visibled ? <FiEyeOff /> : <FiEye />}
                          </span>
                        </div>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>
                  </div>

                  <div className="hr-line-dashed my-2"></div>

                  <h5 className="font-weight-bold my-2">Sender</h5>
                  <InputGroup
                    label="From E-mail:"
                    name="fromEmail"
                    type="email"
                  />
                  <InputGroup label="Sender Name:" name="senderName" />

                  <div className="hr-line-dashed my-2"></div>

                  <div className="form-group row mb-1 align-items-center">
                    <label
                      className="col-sm-12 col-md-4 col-lg-3 col-form-label"
                      style={{ textAlign: "left" }}
                    >
                      Active:
                    </label>
                    <div className="col-sm-12 col-md-7 col-lg-6">
                      <Field name="active">
                        {({ field }) => (
                          <RBForm.Check
                            type="switch"
                            id="active-switch"
                            label=""
                            checked={field.value}
                            {...field}
                            className="custom-switch-modified"
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className="form-group row mb-1 align-items-center">
                    <label
                      className="col-sm-12 col-md-4 col-lg-3 col-form-label"
                      style={{ textAlign: "left" }}
                    >
                      Test Connection:
                    </label>
                    <div className="col-sm-12 col-md-7 col-lg-6">
                      <Button
                        variant="primary"
                        onClick={() => {
                          setCurrentFormValues(values);
                          setShowTestModal(true);
                        }}
                      >
                        <FiMail style={{ marginRight: "8px" }} />
                        Test Connection
                      </Button>
                    </div>
                  </div>

                  <div className="hr-line-dashed my-2"></div>

                  <div className="form-group row">
                    <div className="col-sm-12 d-flex justify-content-end">
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => history.push("/settings")}
                        disabled={isSubmitting}
                        className="mr-2"
                        style={{ marginRight: "8px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Col>
      <Modal
        show={showTestModal}
        onHide={() => setShowTestModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Test SMTP Connection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Send a test e-mail using the current SMTP configuration.</p>

          <RBForm.Group>
            <RBForm.Label className="font-weight-bold">
              Recipient E-mail:
            </RBForm.Label>
            <RBForm.Control
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
            />
          </RBForm.Group>

          <div
            style={{
              backgroundColor: "#e7f3fe",
              color: "#31708f",
              padding: "10px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              marginTop: "15px",
              border: "1px solid #bce8f1",
            }}
          >
            <FiInfo
              style={{ marginRight: "10px", color: "#31708f", flexShrink: 0 }}
              size={20}
            />
            <span style={{ fontSize: "14px" }}>
              The test e-mail will be sent using the configured sender address.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleTestConnection}
            disabled={isTesting || !recipientEmail}
          >
            <FiSend style={{ marginRight: "8px" }} />
            Send Test
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
}
